import type { HTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'
import s from './IconTile.module.css'

export type IconTileSize = 'sm' | 'md' | 'row' | 'lg' | 'xl'
/**
 * The CATEGORICAL palette (#2502 crew contracts, c.84012): eight closed tones,
 * `--cat-1` … `--cat-8`, declared once in `global.css` with light and dark
 * values. A category is a KIND of thing — a feed type, a space — never a state,
 * so it never borrows `--good`/`--warn`. Which category gets which tone is DATA
 * (a feed source's or a navigation group's `tone`), never a component literal.
 */
export type CategoricalTone = 'cat-1' | 'cat-2' | 'cat-3' | 'cat-4' | 'cat-5' | 'cat-6' | 'cat-7' | 'cat-8'
/** What the tile's ground says. `neutral` is the entity mark on the page's
 *  ground (every existing caller); `brand` is a dashboard card's head mark;
 *  the three STATE tones are the attention card's mark; a CATEGORICAL tone is
 *  a feed type's or a space's mark (#2502); `recess` is a link row's brand
 *  glyph SEATED into its plate (#2502, the "Need a hand?" rows). */
export type IconTileTone = 'neutral' | 'brand' | 'recess' | 'info' | 'warn' | 'critical' | CategoricalTone

export interface IconTileProps extends HTMLAttributes<HTMLSpanElement> {
  /** The mark: a symbol, initials, an icon element. */
  children: ReactNode
  /** 22 / 26 / 28 / 30 / 38px. Defaults to `md`, the size ten call sites already
   *  use. `row` (28px) is the Timeline row's type tile (#2502, briefing 03 §4). */
  size?: IconTileSize
  /**
   * The tile's tint (#2406, "Dashboards and Cards"). A card head's mark is a
   * 26px tile in the brand tint with a brand glyph; an attention card's mark is
   * the same tile in its STATE tint. Both were about to become private classes
   * that re-paint this component from outside — the one move the kit rule
   * forbids — so the tile takes the axis instead. Defaults to `neutral`, which
   * is exactly what every existing caller draws.
   */
  tone?: IconTileTone
}

/**
 * THE ENTITY MARK IN A TILE (#2406).
 *
 * A small square that gives a one-character mark a box, so a grid cell's label
 * starts at the same x whether or not the record has a mark.
 *
 * ── why this exists, which is not "it seemed reusable" ─────────────────────
 * The rule was written FOUR times: `Lookup.module.css:55-56`,
 * `RelationField.module.css:16-17`, `RelationPanel.module.css:15-16` and
 * `crm.module.css:132-133` — the last being character-for-character identical
 * to the first, with a comment recording that the component it belonged to had
 * already moved into this package. The copy was left behind when the original
 * went.
 *
 * `packages/ui/src/conformance/typeScale.test.ts:118,125,126` is the part worth
 * pausing on: it classifies three of those four as `klass: 'glyph'` and
 * describes them as "the entity mark in a 26px tile", "…22px tile", "…30px
 * tile". The kit had written this component's documentation, in a census, for
 * a component that did not exist. That census reads only `packages/ui`, so the
 * console's copy and its two inline-resized variants were never in it.
 *
 * ── the size axis ──────────────────────────────────────────────────────────
 * Four sizes because four exist, not because four seemed like a good number.
 * `xl` is why this is a component rather than a utility class: six peek headers
 * write `style={{ width: 38, height: 38, fontSize: 17 }}` by hand, overriding
 * three of the rule's ten declarations with the same three numbers every time.
 * A class cannot absorb that; a prop can.
 *
 * ── what it is not ─────────────────────────────────────────────────────────
 * Not `Badge` or `Pill` — those are `border-radius: 999px` text chips. Not
 * `EntityIcon`, which renders the mark itself and no box. Not `Surface`, which
 * is a padded panel with no size and no centring. Nothing in the kit was this.
 */
export function IconTile({ children, size = 'md', tone = 'neutral', className, ...rest }: IconTileProps) {
  return (
    <span className={cx(s.tile, s[size], tone !== 'neutral' && s[tone], className)} {...rest}>
      {children}
    </span>
  )
}
